import {
  addDays,
  formatLocalDateInput,
  parseLocalDateInput,
  startOfLocalDay,
} from "./subscriptionPeriodDate";

export const EVENT_TOTALS_KEYS = ["VERIFY", "INCIDENT", "DEFECT", "PRINT"];

export const EVENT_TOTALS_LABELS = {
  VERIFY: "Верификация",
  INCIDENT: "Инциденты",
  DEFECT: "Брак",
  PRINT: "Напечатано",
};

export const EVENT_TOTALS_SHORT_LABELS = {
  VERIFY: "Вериф.",
  INCIDENT: "Инц.",
  DEFECT: "Брак",
  PRINT: "Печать",
};

export function normalizeTotals(totals) {
  const t = totals && typeof totals === "object" ? totals : {};
  return {
    VERIFY: Number(t.VERIFY ?? t.verify) || 0,
    INCIDENT: Number(t.INCIDENT ?? t.incident) || 0,
    DEFECT: Number(t.DEFECT ?? t.defect) || 0,
    PRINT: Number(t.PRINT ?? t.print) || 0,
  };
}

export function getCalendarMonthRangeToToday() {
  const today = startOfLocalDay();
  const from = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    from: formatLocalDateInput(from),
    to: formatLocalDateInput(today),
  };
}

/** Скользящие 30 календарных дней: from = today − 30, to = today. */
export function getRolling30DaysRangeToToday() {
  const today = startOfLocalDay();
  const from = addDays(today, -30);
  return {
    from: formatLocalDateInput(from),
    to: formatLocalDateInput(today),
  };
}

/**
 * Нормализует message отчёта event_totals_sa (массив companies или одна компания).
 * @returns {{ period: object | null, companies: { company_id: number, totals: object }[] }}
 */
export function normalizeEventTotalsMessage(message) {
  if (!message || typeof message !== "object") {
    return { period: null, companies: [] };
  }
  if (Array.isArray(message.companies)) {
    return {
      period: message.period ?? null,
      companies: message.companies,
    };
  }
  if (message.company_id != null && message.totals) {
    return {
      period: message.period ?? null,
      companies: [
        {
          company_id: message.company_id,
          totals: message.totals,
        },
      ],
    };
  }
  return {
    period: message.period ?? null,
    companies: [],
  };
}

/**
 * 6 календарных месяцев (текущий + 5 предыдущих), с 1-го числа начального месяца по сегодня.
 */
export function getHalfYearRangeToToday() {
  const today = startOfLocalDay();
  const from = new Date(today.getFullYear(), today.getMonth() - 5, 1);
  return {
    from: formatLocalDateInput(from),
    to: formatLocalDateInput(today),
  };
}

export function inclusiveDayCount(fromIso, toIso) {
  const from = parseLocalDateInput(fromIso);
  const to = parseLocalDateInput(toIso);
  const ms = to.getTime() - from.getTime();
  if (Number.isNaN(ms)) {
    return 1;
  }
  return Math.max(1, Math.floor(ms / (24 * 60 * 60 * 1000)) + 1);
}

function roundAvg(value) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value * 10) / 10;
}

export function totalsToAvgPerDay(totals, fromIso, toIso) {
  const days = inclusiveDayCount(fromIso, toIso);
  const n = normalizeTotals(totals);
  return {
    VERIFY: roundAvg(n.VERIFY / days),
    INCIDENT: roundAvg(n.INCIDENT / days),
    DEFECT: roundAvg(n.DEFECT / days),
    PRINT: roundAvg(n.PRINT / days),
  };
}

/** Среднее в сутки для дашборда «Мои компании» — только целые числа. */
export function totalsToAvgPerDayInteger(totals, fromIso, toIso) {
  const days = inclusiveDayCount(fromIso, toIso);
  const n = normalizeTotals(totals);
  return {
    VERIFY: Math.round(n.VERIFY / days),
    INCIDENT: Math.round(n.INCIDENT / days),
    DEFECT: Math.round(n.DEFECT / days),
    PRINT: Math.round(n.PRINT / days),
  };
}

export function pickCompanyTotals(companies, companyId) {
  if (!Array.isArray(companies)) {
    return normalizeTotals(null);
  }
  const row = companies.find((c) => Number(c.company_id) === Number(companyId));
  return normalizeTotals(row?.totals);
}

export function buildCompanyTotalsMap(companies) {
  const map = new Map();
  if (!Array.isArray(companies)) {
    return map;
  }
  for (const row of companies) {
    if (row?.company_id != null) {
      map.set(Number(row.company_id), normalizeTotals(row.totals));
    }
  }
  return map;
}

export function canViewEventTotals(RolesState, user) {
  return (
    RolesState.all_soft_client ||
    RolesState.subscription_rights ||
    user?.is_superuser === true
  );
}

function formatIntegerWithSpaces(intPart) {
  const sign = intPart.startsWith("-") ? "-" : "";
  const digits = sign ? intPart.slice(1) : intPart;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return sign + grouped;
}

/** Числа событий: группы по 3 разряда через пробел (тысячи, миллионы, …). */
export function formatEventTotalDisplay(value) {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  if (Number.isInteger(value)) {
    return formatIntegerWithSpaces(String(value));
  }
  const rounded = Math.round(value * 10) / 10;
  const [intPart, decPart] = String(rounded).split(".");
  const formattedInt = formatIntegerWithSpaces(intPart);
  return decPart != null ? `${formattedInt},${decPart}` : formattedInt;
}

/** @returns {number|null} null если VERIFY+DEFECT === 0 */
export function defectSharePercent(verify, defect) {
  const v = Number(verify) || 0;
  const d = Number(defect) || 0;
  const total = v + d;
  if (total === 0) {
    return null;
  }
  return Math.round((d / total) * 1000) / 10;
}

function formatPercentOneDecimal(percent) {
  const rounded = Math.round(percent * 10) / 10;
  const [intPart, decPart] = String(rounded).split(".");
  return decPart != null ? `${intPart},${decPart}%` : `${intPart}%`;
}

/** Ячейка «Брак» на дашборде: абсолют / доля от (VERIFY+DEFECT). */
export function formatDefectWithShareDisplay(totals) {
  const n = normalizeTotals(totals);
  const abs = formatEventTotalDisplay(n.DEFECT);
  const share = defectSharePercent(n.VERIFY, n.DEFECT);
  if (share == null) {
    return `${abs} / 0%`;
  }
  return `${abs} / ${formatPercentOneDecimal(share)}`;
}

export function isDefectShareAtLeast(totals, minPercent) {
  const n = normalizeTotals(totals);
  const share = defectSharePercent(n.VERIFY, n.DEFECT);
  if (share == null) {
    return false;
  }
  return share >= minPercent;
}
